import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Add } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import {
  useEquipmentActions,
  useEquipmentDelivery,
  EquipmentDeliveryForm,
  EquipmentDelivery,
  Section,
} from 'modules/equipment';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { DealBuilderFormValues } from '../../../types';
import { useEquipmentSearch } from '../../../hooks';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { mapEquipmentSourceToItem, isSameDeliveryAddress } from '../../../lib';
import { StepFooterNav } from '../../components/StepFooterNav';
import { EquipmentSearchBar } from './EquipmentSearchBar';
import { EquipmentItemCard } from './EquipmentItemCard';

export const EquipmentStep = () => {
  const { control, setValue, getValues } = useFormContext<DealBuilderFormValues>();
  const selectedId = useWatch({ control, name: 'equipment.selectedId' });
  const { search, results, loading } = useEquipmentSearch();
  const {
    equipmentData,
    selectedEquipmentItem,
    setSelectedEquipmentItem,
    vo,
    deliverySubmitRef,
    activeEntity,
    ensureDealBuilder,
    upsertEquipments,
  } = useDealBuilderContext();
  const { handleOpen: openEquipmentDialog, handleClose: closeEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );
  const { onCreateEquipment, onUpdateEquipment } = useEquipmentActions();
  const lastSavedAddressRef = useRef<EquipmentDelivery | null>(null);
  const { handleUpdateDeliveryAddress } = useEquipmentDelivery(selectedId ?? undefined, address => {
    lastSavedAddressRef.current = address;
  });

  // reset the tracked address when a different equipment item is selected
  useEffect(() => {
    lastSavedAddressRef.current = null;
  }, [selectedId]);

  // Create deal builder entity if one doesn't exist yet
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (activeEntity || hasStartedRef.current) return;
    hasStartedRef.current = true;
    const { ownerPgIds, accountContactIds } = getValues('contacts');
    const contacts = [
      ...ownerPgIds.map(id => ({ ownerPgId: id })),
      ...accountContactIds.map(id => ({ accountContactId: id })),
    ];
    const currentSelectedId = getValues('equipment.selectedId');
    ensureDealBuilder(contacts).then(newProposalId => {
      if (newProposalId && currentSelectedId) {
        upsertEquipments([currentSelectedId], newProposalId);
      }
    });
  }, [activeEntity, getValues, ensureDealBuilder, upsertEquipments]);

  const handleDeliveryBlur = useCallback(async () => {
    if (!selectedId || !deliverySubmitRef.current) return;
    const result = await deliverySubmitRef.current.submit();
    if (!result.isValid) return;
    if (isSameDeliveryAddress(lastSavedAddressRef.current, result.formValues)) return;
    await handleUpdateDeliveryAddress(result.formValues);
    lastSavedAddressRef.current = result.formValues;
  }, [selectedId, deliverySubmitRef, handleUpdateDeliveryAddress]);

  const prepopulatedItems = useMemo(() => (equipmentData ?? []).map(mapEquipmentSourceToItem), [equipmentData]);

  const displayItems = useMemo(() => {
    const base = results.length > 0 ? results : prepopulatedItems;
    if (selectedEquipmentItem && !base.some(i => i.equipment.id === selectedEquipmentItem.equipment.id)) {
      return [selectedEquipmentItem, ...base];
    }
    return base;
  }, [results, prepopulatedItems, selectedEquipmentItem]);

  const handleSelect = (id: string) => {
    if (selectedId === id) {
      return;
    } else {
      const item = displayItems.find(i => i.equipment.id === id) ?? null;
      setValue('equipment.selectedId', id);
      setSelectedEquipmentItem(item);
      upsertEquipments([id]);
    }
  };

  const handleViewUnit = (equipmentId: string) => {
    window.open(`/inventory/${equipmentId}`, '_blank');
  };

  const handleAddManualUnit = () => {
    openEquipmentDialog({
      equipmentId: undefined,
      onCancel: closeEquipmentDialog,
      onSubmit: async ({ createDTO }) => {
        closeEquipmentDialog();
        await onCreateEquipment({
          vendorOpportunityId: vo?.vendorOpportunityId ?? '',
          dynamicsVendorOpportunityId: vo?.dynamicsVendorOpportunityId ?? '',
          ...createDTO,
        });
      },
    });
  };

  return (
    <CardContainer
      title="Add Equipment"
      subTitle="Select units from inventory or add them manually to include in this quote."
    >
      <Stack gap={2}>
        <EquipmentSearchBar onChange={search} />

        {loading && <Typography color="text.secondary">Searching...</Typography>}

        {displayItems.length === 0 ? (
          <Typography variant="subtitle2" component="span" fontWeight={500}>
            No equipment added. Search Inventory or add equipment manually.
          </Typography>
        ) : (
          <Stack gap={1.5}>
            {displayItems.map(item => (
              <EquipmentItemCard
                key={item.equipment.id}
                item={item}
                selected={selectedId === item.equipment.id}
                onSelect={handleSelect}
                onViewUnit={handleViewUnit}
              />
            ))}
          </Stack>
        )}

        <Button size="small" startIcon={<Add />} onClick={handleAddManualUnit} sx={{ alignSelf: 'flex-start' }}>
          Add Manual Unit
        </Button>
      </Stack>

      {selectedId && (
        <Section title="Delivery Address">
          <Box onBlur={handleDeliveryBlur}>
            <EquipmentDeliveryForm equipmentId={selectedId} submitRef={deliverySubmitRef} />
          </Box>
        </Section>
      )}

      <StepFooterNav />
    </CardContainer>
  );
};
